import { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import {
  type UniversalIssueWithScreenshot,
  type Filters,
  type OtherFilters,
  impactValues,
  type Impact,
  type BaseFilters,
  configBestPracticesValues,
  configNeedsReviewValues,
  igtValues,
  filterUniversalIssues
} from '@deque/axe-devtools-issues-utils';
import { standardKeys } from '../utils/rulesets';
import rulesets, { type Standards } from '@deque/accessibility-standards';
import { getGuides } from '../utils/get-guides';
import type { TFunction } from 'react-i18next';
import { type ImpactValue } from 'axe-core';

export const defaultBaseFilters: BaseFilters = {
  configBestPractices: 'disabled',
  configNeedsReview: 'disabled',
  configAccessibilityStandard: 'wcag21aa',
  newIssues: false,
  pageStateKey: ''
};

export const allOffOtherFilters: OtherFilters = {
  bestPractices: false,
  automatic: false,
  needsReview: false,
  impact: [],
  manual: false
};

export const allOnOtherFilters: Required<OtherFilters> = {
  bestPractices: true,
  automatic: true,
  needsReview: true,
  impact: [...impactValues],
  igt: 'all',
  manual: true
};

export type FilterKey = keyof Filters;
export type FilterValue = boolean | string;

type UseIssueFilterOptions = {
  defaultOtherFilters?: Partial<OtherFilters>;
};

const getOneOfSearchParam = <T,>(
  searchParams: URLSearchParams,
  param: keyof Filters,
  allowedValues: ReadonlyArray<T>,
  defaultValue: T
): T =>
  searchParams.has(param) &&
  allowedValues.includes(searchParams.get(param) as unknown as T)
    ? (searchParams.get(param) as unknown as T)
    : defaultValue;

/**
 * Search the query params for multiple desired values, returning the first value in the params array that matches a param.
 * returns the first matching param, in order of the values in the params aray.
 *
 * ```ts
 *  const searchParams = new URLSearchParams('?foo=bar&baz=qux&quux=corge');
 *  getFirstMatchingParam(searchParams, ['foo', 'baz', 'quux'], ['corge'], 'qux'); // returns 'quux'
 * ```
 * @param searchParams
 * @param params
 * @param allowedValues
 * @param defaultValue
 * @returns the first matching param, in order of the values in the params aray, or the `defaultValue`.
 */
const getFirstMatchingParam = <T,>(
  searchParams: URLSearchParams,
  params: FilterKey[],
  allowedValues: ReadonlyArray<T>,
  defaultValue: T
): T => {
  const matchingParam = params.find(
    param =>
      searchParams.has(param) &&
      allowedValues.includes(searchParams.get(param) as T)
  );

  if (matchingParam) {
    return searchParams.get(matchingParam) as T;
  }

  return defaultValue;
};

const getBooleanSearchParam = (
  searchParams: URLSearchParams,
  param: keyof Filters,
  defaultValue: boolean
): boolean =>
  searchParams.has(param) &&
  ['true', 'false'].includes(searchParams.get(param) as string)
    ? (searchParams.get(param) as string) === 'true'
    : defaultValue;

const getImpactSearchParam = (
  searchParams: URLSearchParams,
  defaultValue: Impact
): Impact => {
  const impactParam = searchParams.get('impact') as string;
  const impactParamValues = impactParam?.toLowerCase().split(',') || [];
  const impact =
    (impactParamValues as Impact).filter(value =>
      impactValues.includes(value)
    ) || [];

  return impact.length ? impact : defaultValue;
};

export const getFiltersFromSearchParams = (
  searchParams: URLSearchParams,
  options: UseIssueFilterOptions = {}
): Filters => {
  const hasOtherFilter = Object.keys(allOnOtherFilters).some(otherFilter =>
    searchParams.has(otherFilter)
  );
  const defaultOtherFilters = {
    ...(hasOtherFilter ? allOffOtherFilters : allOnOtherFilters),
    ...options.defaultOtherFilters
  };

  const accessibilityStandard = getFirstMatchingParam(
    searchParams,
    ['configAccessibilityStandard', 'configWCAGLevel'],
    standardKeys,
    defaultBaseFilters.configAccessibilityStandard
  );

  const baseFilters: BaseFilters = {
    configAccessibilityStandard: accessibilityStandard,
    manifestId: searchParams.get('manifestId') || defaultBaseFilters.manifestId,
    configBestPractices: getOneOfSearchParam(
      searchParams,
      'configBestPractices',
      configBestPracticesValues,
      defaultBaseFilters.configBestPractices
    ),
    configNeedsReview: getOneOfSearchParam(
      searchParams,
      'configNeedsReview',
      configNeedsReviewValues,
      defaultBaseFilters.configNeedsReview
    ),
    newIssues: getBooleanSearchParam(
      searchParams,
      'newIssues',
      !!defaultBaseFilters.newIssues
    ),
    pageStateKey:
      searchParams.get('pageStateKey') || defaultBaseFilters.pageStateKey
  };

  const otherFilters: OtherFilters = {
    igt: getOneOfSearchParam(
      searchParams,
      'igt',
      igtValues,
      defaultOtherFilters.igt
    ),
    automatic: getBooleanSearchParam(
      searchParams,
      'automatic',
      defaultOtherFilters.automatic
    ),
    bestPractices:
      baseFilters.configBestPractices === 'disabled'
        ? false
        : getBooleanSearchParam(
            searchParams,
            'bestPractices',
            defaultOtherFilters.bestPractices
          ),
    needsReview:
      baseFilters.configNeedsReview === 'disabled'
        ? false
        : getBooleanSearchParam(
            searchParams,
            'needsReview',
            defaultOtherFilters.needsReview
          ),
    impact: getImpactSearchParam(searchParams, defaultOtherFilters.impact),
    manual: getBooleanSearchParam(
      searchParams,
      'manual',
      defaultOtherFilters.manual
    )
  };

  return {
    ...baseFilters,
    ...otherFilters
  };
};

export const getSearchParamsFromFilters = (
  filters: Partial<Filters>
): URLSearchParams => {
  const searchParams = new URLSearchParams();

  let property: keyof typeof filters;
  for (property in filters) {
    const value = filters[property];
    if (Array.isArray(value)) {
      if (value.length) {
        searchParams.set(property, value.join(','));
      }
    } else if (value !== undefined && value !== null) {
      searchParams.set(property, value.toString());
    }
  }
  return searchParams;
};

export const getBaseFilters = (filters: Filters): BaseFilters => ({
  configNeedsReview: filters.configNeedsReview,
  configBestPractices: filters.configBestPractices,
  configAccessibilityStandard: filters.configAccessibilityStandard
});

export const getFilterLabels = (t: TFunction) => {
  const accessibilityStandard = {
    label: t('Accessibility Standard'),
    values: new Map([
      ...(Object.keys(rulesets).map(key => {
        const ruleset = rulesets[key as Standards].name;
        return [key, ruleset];
      }) as [Standards, string][])
    ])
  };

  const booleanValues = new Map([
    [false, t('Off')],
    [true, t('On')]
  ]);

  return {
    configAccessibilityStandard: accessibilityStandard,
    configWCAGLevel: accessibilityStandard,
    manifestId: {
      label: t('IGT'),
      values: new Map()
    },
    igt: {
      label: t('IGT'),
      values: new Map([
        ['all', t('All')],
        ...getGuides(t).map(
          guide => [guide.name, guide.heading] as [string, string]
        )
      ])
    },
    automatic: {
      label: t('Automatic'),
      values: booleanValues
    },
    configBestPractices: {
      label: t('Best Practices'),
      values: new Map([
        ['enabled', t('SHOWN')],
        ['disabled', t('HIDDEN')]
      ])
    },
    bestPractices: {
      label: t('Best Practices'),
      values: booleanValues
    },
    configNeedsReview: {
      label: t('Needs Review'),
      values: new Map([
        ['disabled', t('DISABLED')],
        ['enabled', t('ENABLED')],
        ['exclude', t('EXCLUDED (from issue totals)')]
      ])
    },
    needsReview: {
      label: t('Needs Review'),
      values: booleanValues
    },
    impact: {
      label: t('Impact'),
      values: new Map([
        ['all', t('All')],
        ['critical', t('Critical')],
        ['serious', t('Serious')],
        ['moderate', t('Moderate')],
        ['minor', t('Minor')],
        ['none', t('None')]
      ])
    },
    manual: {
      label: t('Manual'),
      values: booleanValues
    },
    newIssues: {
      label: t('New Issues Only'),
      values: booleanValues
    },
    pageStateKey: {
      label: t('Page State'),
      values: new Map()
    }
  };
};

export const getFilterValues = (filter: FilterKey, t: TFunction) => {
  const filterLabels = getFilterLabels(t) || {};

  return (
    filterLabels[filter] as {
      label: string;
      values: Map<FilterValue, string>;
    }
  ).values;
};

export const getImpactValue = (impact: Impact | undefined, t: TFunction) => {
  if (!impact || impact.length === 0) {
    return t('None');
  }

  if (impact.length === impactValues.length) {
    return t('All');
  }

  const impactVals = getFilterValues('impact', t);

  const labels: string[] = [];
  impactVals?.forEach((value, key) => {
    if (impact.includes(key as keyof ImpactValue)) {
      labels.push(value);
    }
  });

  return labels.join(', ');
};

export type UpdateFilter = (key: FilterKey, value: FilterValue) => void;
export type UpdateEntireFilter = (values: [FilterKey, FilterValue][]) => void;

export interface State {
  filteredIssues: UniversalIssueWithScreenshot[];
  totalIssues: UniversalIssueWithScreenshot[];
  filters: Filters;
  searchParams: URLSearchParams;
  updateFilter: UpdateFilter;
  updateEntireFilter: UpdateEntireFilter;
}

const useIssuesFilter = (
  issues: UniversalIssueWithScreenshot[],
  options: UseIssueFilterOptions = {}
): State => {
  const history = useHistory();
  const [search, setSearch] = useState(history.location.search);

  useEffect(() => {
    return history.listen(() => {
      setSearch(history.location.search);
    });
  }, [history]);

  const filters = useMemo<Filters>(
    () => getFiltersFromSearchParams(new URLSearchParams(search), options),
    [search]
  );
  const searchParams = useMemo<URLSearchParams>(
    () => getSearchParamsFromFilters(filters),
    [filters]
  );
  const { filteredIssues, totalIssues } = useMemo(
    () => filterUniversalIssues(issues, filters),
    [filters, issues]
  );

  const updateFilter = useCallback<UpdateFilter>(
    (key, value) => {
      const updatedSearch = new URLSearchParams(search);
      updatedSearch.set(key, value.toString());
      history.push({ search: updatedSearch.toString() });
    },
    [history, search]
  );

  const updateEntireFilter = useCallback<UpdateEntireFilter>(
    values => {
      const updatedSearch = new URLSearchParams(search);

      values.forEach(([key, value]) => {
        updatedSearch.set(key, value.toString());
      });

      history.push({ search: updatedSearch.toString() });
    },
    [history, search]
  );

  return {
    filters,
    searchParams,
    filteredIssues,
    totalIssues,
    updateFilter,
    updateEntireFilter
  };
};

export default useIssuesFilter;
